import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    styled,
    CircularProgress
} from '@mui/material';
import ProcessMapUploadForm from '../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import { X } from 'lucide-react';
import { apiService } from '../../../api/apiService';
import useFunctionOptions from '../utils/useFunctionOptions';
import SuccessPopup from './Success&ErrorPopup/SuccessPopup';
import ErrorPopup from './Success&ErrorPopup/ErrorPopup';

interface ProcessFile {
    id: string;
    name: string;
    date: string;
    functionL1: string;
    functionL2: string;
    functionL3: string;
    status: string;
    size: string;
}

interface ProcessMapPopupProps {
    openUpload: boolean;
    setOpenUpload: Dispatch<SetStateAction<boolean>>;
    management: string;
    onSuccess?: () => void; // Add this line
    fileUploadText?:any;


}

const StyledMenuItem = styled(MenuItem)({
    color: 'black',
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
});

const ProcessMapPopup: React.FC<ProcessMapPopupProps> = ({ openUpload, setOpenUpload, management, onSuccess, fileUploadText }) => {
    const { instance } = useMsal(); // Get the MSAL instance for authentication
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [files, setFiles] = useState<ProcessFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [functionL1, setFunctionL1] = useState('');
    const [functionL2, setFunctionL2] = useState('');
    const [functionL3, setFunctionL3] = useState('');

    const { options: l1Options, loading: l1Loading } = useFunctionOptions('l1', '');
    const { options: l2Options, loading: l2Loading } = useFunctionOptions('l2', functionL1);
    const { options: l3Options, loading: l3Loading } = useFunctionOptions('l3', functionL2);

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
    };

    const handleSubmit = async () => {
        if (selectedFile) {
            const formData = new FormData();
            const processMapReq = {
                manager_id: "some_manager_id",
                management: management,
                function_l1: functionL1,
                function_l2: functionL2,
                function_l3: functionL3,
                doc_path: selectedFile.name,
            };

            formData.append("process_map_req", JSON.stringify(processMapReq));
            formData.append("file", selectedFile);

            setUploading(true);

            try {
                const result = await apiService(`/management/process_map`, "POST", formData, undefined, instance as PublicClientApplication);

                setFiles([
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        name: selectedFile.name,
                        date: new Date().toLocaleDateString(),
                        functionL1,
                        functionL2,
                        functionL3,
                        status: "Submitted",
                        size: `${Math.round(selectedFile.size / 1024)} KB`,
                    },
                    ...files,
                ]);

                setOpenUpload(false);
                setShowSuccess(true);
                resetUploadState();
                if (onSuccess) {
                    onSuccess(); // Call the callback function after successful upload
                }
            } catch (error) {
                console.error("Error during submission:", error);
                setErrorMessage("An error occurred during submission. Please try again.");
                setShowError(true);
            } finally {
                setUploading(false);
            }
        }
    };

    const resetUploadState = () => {
        setSelectedFile(null);
        setFunctionL1('');
        setFunctionL2('');
        setFunctionL3('');
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showSuccess) {
            timer = setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showSuccess]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showError) {
            timer = setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showError]);

    return (
        <>
            <Dialog
                open={openUpload}
                onClose={() => {
                    setOpenUpload(false);
                    resetUploadState();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000',fontWeight:'600',fontSize:'16px' }}>
                    Upload Process Map
                    <IconButton onClick={() => setOpenUpload(false)}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '48% 48%', justifyContent: 'space-between' }}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Function L1 *</InputLabel>
                                <Select
                                    sx={{ borderRadius: '8px', color: '#000' }}
                                    value={functionL1}
                                    label="Function L1 *"
                                    onChange={(e) => setFunctionL1(e.target.value)}
                                >
                                    <StyledMenuItem value="">Select Function L1</StyledMenuItem>
                                    {l1Loading ? (
                                        <StyledMenuItem value="">
                                            <CircularProgress size={24} />
                                        </StyledMenuItem>
                                    ) : (
                                        l1Options.map((option, index) => (
                                            <StyledMenuItem key={index} value={option}>
                                                {option}
                                            </StyledMenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Function L2 *</InputLabel>
                                <Select
                                    sx={{ borderRadius: '8px', color: '#000' }}
                                    value={functionL2}
                                    label="Function L2 *"
                                    onChange={(e) => setFunctionL2(e.target.value)}
                                    disabled={!functionL1}
                                >
                                    <StyledMenuItem value="">Select Function L2</StyledMenuItem>
                                    {l2Loading ? (
                                        <StyledMenuItem value="">
                                            <CircularProgress size={24} />
                                        </StyledMenuItem>
                                    ) : (
                                        l2Options.map((option, index) => (
                                            <StyledMenuItem key={index} value={option}>
                                                {option}
                                            </StyledMenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '48% 48%', justifyContent: 'space-between' }}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Function L3 *</InputLabel>
                                <Select
                                    sx={{ borderRadius: '8px', color: '#000' }}
                                    value={functionL3}
                                    label="Function L3 *"
                                    onChange={(e) => setFunctionL3(e.target.value)}
                                    disabled={!functionL2}
                                >
                                    <StyledMenuItem value="">Select Function L3</StyledMenuItem>
                                    {l3Loading ? (
                                        <StyledMenuItem value="">
                                            <CircularProgress size={24} />
                                        </StyledMenuItem>
                                    ) : (
                                        l3Options.map((option, index) => (
                                            <StyledMenuItem key={index} value={option}>
                                                {option}
                                            </StyledMenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                    <ProcessMapUploadForm
                        file={selectedFile}
                        onFileSelect={handleFileSelect}
                        onFileRemove={() => {
                            setSelectedFile(null);
                        }}
                        fileTitle={fileUploadText}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant='outlined' onClick={() => setOpenUpload(false)} sx={{ color: '#000', borderColor: '#000' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedFile || !functionL1 || !functionL2 || !functionL3 || uploading}

                        sx={{
                            color: 'white',
                            bgcolor: 'black',
                            '&:hover': { bgcolor: 'black' },
                            '&:disabled': { bgcolor: 'grey.300' },
                        }}
                    >
                        {uploading ? 'Uploading...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Popup */}
            <SuccessPopup
                open={showSuccess}
                onClose={() => setShowSuccess(false)}
                message="Process map uploaded successfully!"
            />

            {/* Error Popup */}
            <ErrorPopup
                open={showError}
                onClose={() => setShowError(false)}
                message={errorMessage}
            />
        </>
    );
};

export default ProcessMapPopup;